// src/lib/forfeoPortals.ts
// Hub centralisé des portails Forfeo pour la barre intelligente.
// Objectif : permettre à un utilisateur connecté de revenir au bon dashboard
// sans devoir se déconnecter/reconnecter.

import { normalizeForfeoRole as normalizeCanonicalForfeoRole } from './forfeoCanonicalIdentity';

export type ForfeoPortalId =
  | 'traveler'
  | 'business'
  | 'habitat_landlord'
  | 'habitat_tenant'
  | 'forfeo_pro'
  | 'affiliate'
  | 'prp'
  | 'admin';

export type ForfeoPortalIconKey =
  | 'traveler'
  | 'business'
  | 'habitat'
  | 'pro'
  | 'affiliate'
  | 'prp'
  | 'admin';

export interface ForfeoPortalAccess {
  id: ForfeoPortalId;
  label: string;
  subtitle: string;
  to: string;
  activePrefixes: string[];
  iconKey: ForfeoPortalIconKey;
  priority: number;
}

export const FORFEO_LAST_PORTAL_STORAGE_KEY = 'forfeo:last-connected-portal:v1';

const basePortals: Record<ForfeoPortalId, ForfeoPortalAccess> = {
  traveler: {
    id: 'traveler',
    label: 'Club voyageur',
    subtitle: 'Achats, réservations et favoris',
    to: '/dashboard-voyageur',
    activePrefixes: ['/dashboard-voyageur', '/mon-compte', '/club', '/mes-achats', '/mes-reservations', '/mes-favoris', '/mes-alertes', '/voucher'],
    iconKey: 'traveler',
    priority: 10,
  },
  business: {
    id: 'business',
    label: 'Portail commerçant',
    subtitle: 'Offres, commandes et validations',
    to: '/business-dashboard',
    activePrefixes: ['/business-dashboard'],
    iconKey: 'business',
    priority: 20,
  },
  habitat_landlord: {
    id: 'habitat_landlord',
    label: 'Habitat propriétaire',
    subtitle: 'Immeubles, loyers et locataires',
    to: '/habitat/proprio/dashboard',
    activePrefixes: ['/habitat/proprio/dashboard'],
    iconKey: 'habitat',
    priority: 30,
  },
  habitat_tenant: {
    id: 'habitat_tenant',
    label: 'Habitat locataire',
    subtitle: 'Logement, documents et demandes',
    to: '/habitat/locataire/dashboard',
    activePrefixes: ['/habitat/locataire/dashboard'],
    iconKey: 'habitat',
    priority: 31,
  },
  forfeo_pro: {
    id: 'forfeo_pro',
    label: 'Forfeo Pros',
    subtitle: 'Demandes de service Habitat',
    to: '/forfeopro/dashboard',
    activePrefixes: ['/forfeopro/dashboard'],
    iconKey: 'pro',
    priority: 40,
  },
  affiliate: {
    id: 'affiliate',
    label: 'Ambassadeur',
    subtitle: 'Référencement et commissions',
    to: '/affiliate-dashboard',
    activePrefixes: ['/affiliate-dashboard'],
    iconKey: 'affiliate',
    priority: 50,
  },
  prp: {
    id: 'prp',
    label: 'Directeur PRP',
    subtitle: 'Région, partenaires et commissions',
    to: '/prp-dashboard',
    activePrefixes: ['/prp-dashboard'],
    iconKey: 'prp',
    priority: 60,
  },
  admin: {
    id: 'admin',
    label: 'Admin Forfeo',
    subtitle: 'Contrôle, sécurité et opérations',
    to: '/admin-forfeo-portal',
    activePrefixes: ['/admin-forfeo-portal', '/admin'],
    iconKey: 'admin',
    priority: 70,
  },
};

export const getForfeoPortalDefinition = (id: ForfeoPortalId) => basePortals[id];

export const getForfeoPortalForPath = (pathname: string): ForfeoPortalAccess | null => {
  const cleanPath = pathname || '/';
  const portal = Object.values(basePortals).find((item) =>
    item.activePrefixes.some((prefix) => cleanPath === prefix || cleanPath.startsWith(`${prefix}/`))
  );

  return portal || null;
};

export const rememberForfeoPortalPath = (pathname: string): ForfeoPortalAccess | null => {
  const portal = getForfeoPortalForPath(pathname);
  if (!portal || typeof window === 'undefined') return portal;

  try {
    window.localStorage.setItem(
      FORFEO_LAST_PORTAL_STORAGE_KEY,
      JSON.stringify({ id: portal.id, to: portal.to, savedAt: new Date().toISOString() })
    );
  } catch {
    // localStorage peut être bloqué par le navigateur. On ignore sans casser le header.
  }

  return portal;
};

export const readRememberedForfeoPortal = (): ForfeoPortalAccess | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(FORFEO_LAST_PORTAL_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { id?: ForfeoPortalId };
    if (!parsed?.id || !basePortals[parsed.id]) return null;

    return basePortals[parsed.id];
  } catch {
    return null;
  }
};

const hasData = (result: any) => Boolean(result && !result.error && result.data);

const safeMaybeSingle = async (query: any) => {
  try {
    const result = await query;
    return hasData(result) ? result.data : null;
  } catch {
    return null;
  }
};

export const resolveForfeoPortals = async (
  supabaseClient: any,
  user: any,
): Promise<ForfeoPortalAccess[]> => {
  if (!user?.id) return [];

  const userId = String(user.id);
  const portals = new Map<ForfeoPortalId, ForfeoPortalAccess>();

  // Tout utilisateur authentifié conserve son espace voyageur.
  portals.set('traveler', basePortals.traveler);

  const [
    platformProfile,
    business,
    affiliate,
    prp,
    habitatProfile,
    pro,
  ] = await Promise.all([
    safeMaybeSingle(
      supabaseClient
        .from('profiles')
        .select('id, canonical_role, role, user_role, user_type, account_type')
        .eq('id', userId)
        .maybeSingle(),
    ),

    safeMaybeSingle(
      supabaseClient
        .from('businesses')
        .select('id, is_approved')
        .eq('owner_id', userId)
        .maybeSingle(),
    ),

    safeMaybeSingle(
      supabaseClient
        .from('affiliates')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle(),
    ),

    safeMaybeSingle(
      supabaseClient
        .from('prps')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle(),
    ),

    safeMaybeSingle(
      supabaseClient
        .from('habitat_profiles')
        .select('id, role, is_approved')
        .eq('id', userId)
        .maybeSingle(),
    ),

    safeMaybeSingle(
      supabaseClient
        .from('forfeo_pros')
        .select('id')
        .eq('id', userId)
        .maybeSingle(),
    ),
  ]);

  const canonicalProfileRole = normalizeCanonicalForfeoRole(
    platformProfile?.canonical_role ??
    platformProfile?.role ??
    platformProfile?.user_role ??
    platformProfile?.user_type ??
    platformProfile?.account_type,
  );

  // Seul le profil plateforme lu en base peut ouvrir un portail
  // administratif ou de soutien.
  if (canonicalProfileRole === 'admin') {
    portals.set('admin', basePortals.admin);
  }

  if (
    canonicalProfileRole === 'merchant' &&
    business?.is_approved === true
  ) {
    portals.set('business', basePortals.business);
  }

  if (canonicalProfileRole === 'affiliate') {
    portals.set('affiliate', basePortals.affiliate);
  }

  if (canonicalProfileRole === 'prp') {
    portals.set('prp', basePortals.prp);
  }

  if (canonicalProfileRole === 'forfeopro') {
    portals.set('forfeo_pro', basePortals.forfeo_pro);
  }

  if (canonicalProfileRole === 'habitat_landlord') {
    portals.set(
      'habitat_landlord',
      basePortals.habitat_landlord,
    );
  }

  if (canonicalProfileRole === 'habitat_tenant') {
    portals.set(
      'habitat_tenant',
      basePortals.habitat_tenant,
    );
  }

  // Les profils métiers vérifiés peuvent ajouter leurs portails
  // respectifs sans modifier le rôle plateforme.
  if (business?.is_approved === true) {
    portals.set('business', basePortals.business);
  }

  if (affiliate) {
    portals.set('affiliate', basePortals.affiliate);
  }

  if (prp) {
    portals.set('prp', basePortals.prp);
  }

  if (pro) {
    portals.set('forfeo_pro', basePortals.forfeo_pro);
  }

  if (habitatProfile) {
    const habitatRole = normalizeCanonicalForfeoRole(
      habitatProfile.role,
    );

    if (habitatRole === 'habitat_landlord') {
      portals.set(
        'habitat_landlord',
        basePortals.habitat_landlord,
      );
    }

    if (habitatRole === 'habitat_tenant') {
      portals.set(
        'habitat_tenant',
        basePortals.habitat_tenant,
      );
    }
  }

  return Array.from(portals.values()).sort(
    (left, right) => left.priority - right.priority,
  );
};
